import { useMemo } from "react"

import { format } from "date-fns"

import { useDebounce } from "@/hooks"
import i18n from "@/i18n/config"
import useInsuranceQuery from "@/queries/hooks/useInsuranceQuery"
import { convertBirthDateToISO } from "@/utils/dateUtils"

import { ValidatedInsurance } from "../components"
import { isPersonDataComplete } from "../utils"

interface UseInsuranceProps {
  firstName: string
  lastName: string
  birthDate: string
  bookingLengthInDays: number
  resortId: number
  startDate: Date
  isSubscriptionProduct: boolean
  hasFormErrors: boolean
}

const useInsurance = ({
  firstName,
  lastName,
  birthDate,
  bookingLengthInDays,
  resortId,
  startDate,
  isSubscriptionProduct,
  hasFormErrors,
}: UseInsuranceProps) => {
  const personData = useMemo(
    () => ({ firstName, lastName, birthDate }),
    [firstName, lastName, birthDate]
  )
  const debouncedPersonData = useDebounce(personData, 500)

  const hasCompletePersonData = isPersonDataComplete(
    firstName,
    lastName,
    birthDate
  )

  const isUserStillTyping = personData !== debouncedPersonData

  const shouldFetchInsurance = useMemo(() => {
    const hasCompleteDebouncedData = isPersonDataComplete(
      debouncedPersonData.firstName,
      debouncedPersonData.lastName,
      debouncedPersonData.birthDate
    )
    return !hasFormErrors && hasCompleteDebouncedData
  }, [debouncedPersonData, hasFormErrors])

  const { data, isLoading, error } = useInsuranceQuery(
    {
      firstName: debouncedPersonData.firstName,
      lastName: debouncedPersonData.lastName,
      birthDate: convertBirthDateToISO(debouncedPersonData.birthDate) || "",
      bookingLength: bookingLengthInDays,
      resortId,
      arrivalDate: format(startDate, "yyyy-MM-dd"),
      productType: isSubscriptionProduct ? "SUBSCRIPTION" : "SKIPASS",
      cultureName: i18n.language,
    },
    shouldFetchInsurance
  )

  const shouldShowInsurance = hasCompletePersonData && !isUserStillTyping

  const insurance: ValidatedInsurance | null = useMemo(() => {
    if (!shouldShowInsurance || !data?.Success) return null

    const existingPrice = data.ExistingPrice ?? 0
    const adjustedPrice = Math.max(0, data.Price - existingPrice)

    return {
      name: data.Name,
      shortDescription: data.ShortDescription,
      fullDescription: data.FullDescription,
      price: adjustedPrice,
      existingPrice,
      currencySymbol: data.CurrencySymbol,
      analyticsName: data.AnalyticsName,
      analyticsId: data.AnalyticsId,
    }
  }, [data, shouldShowInsurance])

  return {
    insurance,
    isLoading: hasCompletePersonData && (isLoading || isUserStillTyping),
    error,
    isUserStillTyping,
  }
}

export default useInsurance
